import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { IBufferMetrics } from '@bufferController/BufferMetrics/IBufferMetrics';
import { GetSideMetricsAsyncEvents } from '@bufferController/BufferMetrics/helpers/GetSideMetricsAsync.events';
import { TargetBufferEvents } from '@bufferController/PlaybackSpeedController/TargetBuffer.events';
import { EPlaybackState } from '@bufferController/PlaybackStateController/IPlaybackStateController';
import { IPlaybackStateControllerConfig } from '@bufferController/PlaybackStateController/PlaybackStateController.config';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { TEventBus } from '@eventBus/EventBus';
import { ResolutionsManagerEvents } from '@resolutionsManager/ResolutionsManager.events';
import { StreamLoaderModuleEvents } from '@streamLoader/StreamLoader.events';
import { getPartHoldback } from '@utils/common/partHoldback';
import bind from '@utils/decorators/bind';
import { IHydratedManifest } from '@utils/m3u8Hydrator/m3u8Hydrator';

interface ITimePeriod {
  startTS: number;
  endTS: number;
}

export class PlaybackStateController extends ConfigConsumer<IPlaybackStateControllerConfig> {
  private _currentState: EPlaybackState = EPlaybackState.IDLE;
  private _numberOfStallsOnEdge: number = 0;
  private _videoElement: HTMLVideoElement | null = null;
  private _syncOnNextMetrics: boolean = false;
  private _isInStall: boolean = false;
  private _targetLiveEdge: number = 6;
  private _stallTS: number = 0;
  private _startTS: number = 0;
  private _stalledTimeRanges: ITimePeriod[] = [];
  private readonly _onPlaybackMetricsReadyListener: string;
  private readonly _onResolutionChangedListener: string;
  private readonly _onFragmentRequestedListener: string;
  private readonly _stallsToChangeState: number = 3;
  private readonly _onPlaylistInfoAvailableListener: string;
  private readonly _onPlayingTimeFractionRequestedListener: string;

  constructor(eventBus: TEventBus) {
    super('playbackStateController', eventBus);

    this._onPlaybackMetricsReadyListener = this._eventBus.subscribe(
      BufferControllerEvents.bufferMetricsAvailable,
      this._onMetricsAvailable,
    );
    this._onResolutionChangedListener = this._eventBus.subscribe(ResolutionsManagerEvents.resolutionChanged, this.reset);
    this._onFragmentRequestedListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.fragmentLoader.fragments.fragmentLoadingStarted,
      this._onFragmentRequested,
    );
    this._onPlaylistInfoAvailableListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.playlistRotator.playlists.playlistInfo,
      this._onPlaylistInfoAvailable,
    );
    this._onPlayingTimeFractionRequestedListener = this._eventBus.subscribe(
      GetSideMetricsAsyncEvents.getPlayingTimeFraction,
      this._onPlayingTimeFractionRequested,
    );
  }

  destroy(): void {
    super.destroy();

    this._eventBus.unsubscribe(this._onPlaybackMetricsReadyListener);
    this._eventBus.unsubscribe(this._onResolutionChangedListener);
    this._eventBus.unsubscribe(this._onFragmentRequestedListener);
    this._eventBus.unsubscribe(this._onPlaylistInfoAvailableListener);
    this._eventBus.unsubscribe(this._onPlayingTimeFractionRequestedListener);

    this._videoElement?.removeEventListener('canplaythrough', this._onCanPlayThrough);
    this._videoElement?.removeEventListener('play', this._onPlay);
    this._videoElement?.removeEventListener('playing', this._onPlaying);
  }

  public setVideoElement(videoElement: HTMLVideoElement | null): void {
    if (this._videoElement) {
      this._videoElement.removeEventListener('canplaythrough', this._onCanPlayThrough);
      this._videoElement?.removeEventListener('play', this._onPlay);
      this._videoElement?.removeEventListener('playing', this._onPlaying);
    }

    this._videoElement = videoElement;

    this._videoElement?.addEventListener('canplaythrough', this._onCanPlayThrough);
    this._videoElement?.addEventListener('play', this._onPlay);
    this._videoElement?.addEventListener('playing', this._onPlaying);
  }

  @bind
  public reset(): void {
    this._currentState = EPlaybackState.IDLE;
    this._numberOfStallsOnEdge = 0;
  }

  public resetPlayingTimeCollector(): void {
    this._stallTS = 0;
    this._startTS = 0;
    this._stalledTimeRanges = [];
  }

  public _handleConfigUpdated(): void {}

  private _setNextState(nextState: EPlaybackState): void {
    if (nextState === this._currentState) {
      return;
    }

    this._eventBus.publish(BufferControllerEvents.playbackStateChanged, { prevState: this._currentState, nextState: nextState });

    this._currentState = nextState;
  }

  @bind
  private _onFragmentRequested(): void {
    if (this._currentState === EPlaybackState.IDLE) {
      this._setNextState(EPlaybackState.LOADING);
    }
  }

  @bind
  private _onMetricsAvailable(metrics: IBufferMetrics): void {
    if (this._syncOnNextMetrics && metrics.bufferedTimeRanges.length) {
      this._syncOnNextMetrics = false;
      this._syncToLiveEdge(metrics.bufferedTimeRanges.slice(-1)[0].end);
    }

    if (this._currentState === EPlaybackState.IDLE) {
      return;
    }

    if (this._isInStall !== metrics.isStallCandidate) {
      this._isInStall = metrics.isStallCandidate;
      if (metrics.isStallCandidate) {
        this._onStall();
      } else {
        this._onPlaying();
      }
    }

    const isStalledAndOnEdge: boolean = metrics.playbackPositionDelta === 0 && metrics.isOnBufferEdge;

    if (!isStalledAndOnEdge) {
      this._numberOfStallsOnEdge = 0;

      this._setNextState(EPlaybackState.PLAYING);

      return;
    }

    if (isStalledAndOnEdge && this._currentState === EPlaybackState.PLAYING) {
      this._numberOfStallsOnEdge++;
    }

    if (this._numberOfStallsOnEdge >= this._stallsToChangeState) {
      this._setNextState(EPlaybackState.PAUSED);
    }

    if (this._isInStall && this.config.stallDurationToDisableTargetBufferDecrease <= Date.now() - this._stallTS) {
      this._eventBus.publish(TargetBufferEvents.originalTargetBufferDisable, {});
    }
  }

  @bind
  private _onCanPlayThrough(): void {
    if (!this._startTS) {
      this._startTS = Date.now();
    }

    if (this.config.autoPlay) {
      this._videoElement?.play();
    }
  }

  @bind
  private _onPlaylistInfoAvailable(manifest: IHydratedManifest): void {
    this._targetLiveEdge = getPartHoldback(manifest, manifest.targetDuration * 3);
  }

  @bind
  private _onPlay(): void {
    if (this.config.syncToLiveEdge && this._videoElement) {
      if (this._videoElement.buffered.length) {
        this._syncToLiveEdge(this._videoElement.buffered.end(this._videoElement.buffered.length - 1));
      } else {
        this._syncOnNextMetrics = true;
      }
    }
  }

  private _getDesiredPosition(bufferEndPosition: number): number {
    return bufferEndPosition - this.config.syncToLiveDelta * this._targetLiveEdge;
  }

  private _syncToLiveEdge(bufferEndPosition: number): void {
    if (!this._videoElement || !this._videoElement?.buffered.length) {
      return;
    }

    const desiredPosition: number = this._getDesiredPosition(bufferEndPosition);

    if (desiredPosition > this._videoElement.buffered.start(0) && desiredPosition > this._videoElement.currentTime) {
      this._videoElement.currentTime = desiredPosition;
    }
  }

  @bind
  private _onPlaying(): void {
    if (this._stallTS) {
      this._stalledTimeRanges.push({ startTS: this._stallTS, endTS: Date.now() });
      this._stallTS = 0;
    }
  }

  private _onStall(): void {
    this._stallTS = Date.now();
  }

  @bind
  private _onPlayingTimeFractionRequested(): void {
    const playingTimeFraction: number | null = this._calculatePlayingTimeFraction();

    this._eventBus.publish(GetSideMetricsAsyncEvents.getPlayingTimeFractionAvailable, playingTimeFraction);
  }
  private _calculatePlayingTimeFraction(): number | null {
    if (!this._startTS) {
      return null;
    }

    const currentWindow: number = Date.now() - this.config.playingTimeFractionCollectionWindow;

    this._stalledTimeRanges = this._stalledTimeRanges.filter(({ endTS }) => endTS > currentWindow);

    const totalTime: number = Date.now() - this._startTS;
    const timeInStalls: number = this._stalledTimeRanges.reduce((s, c) => s + (c.endTS - c.startTS), 0);
    const currentStallTime: number = this._stallTS ? Date.now() - this._stallTS : 0;
    const playingTime: number = totalTime - timeInStalls - currentStallTime;
    this._startTS = Date.now();
    return playingTime / totalTime;
  }
}
